import $ from 'jquery';

import AlerterView from 'chairisher/view/alerter';
import FolderCardMenu, { FolderCardTransitionAnimations } from 'chairisher/component/card/foldercardmenu';
import Pill from 'chairisher/component/pill';
import SpotlightTypesView from 'chairisher/view/product/spotlighttypes';

import { getCollectionId, getCollectionSortDeleteUrl, getCollectionSortPatchUrl } from 'chairisher/context/collection';
import { getDefaultErrorText } from 'chairisher/context/error';
import { canRenderFolderIcons } from 'chairisher/context/product';
import { FolderCardPosition } from 'chairisher/util/analytics';

/**
 * View that allows for the manipulation of a GridProduct.
 *
 * @param {Object} settings
 * @param {jQuery} settings.$el The jQuery representation of the product
 * @param {GridProductData} settings.data The product's data from the backend
 *
 * @class GridProductView
 * @constructs GridProductView
 */
const GridProductView = function GridProductView(settings) {
    const options = $.extend(
        {
            $el: this.$el,
            data: this.data,
        },
        settings,
    );

    this.$el = options.$el;
    this.data = options.data;
    this.pendingSpotlightData = [];

    this.spotlightTypesView = new SpotlightTypesView({
        $el: this.$el.find('.js-quick-buttons'),
        data: this.data,
    });

    this.bind();
    this.$el.data('gridProductView', this);
};

/**
 * The jQuery representation of the product
 *
 * @type {jQuery}
 */
GridProductView.prototype.$el = null;

/**
 * Flag indicating if the spotlight type buttons are enabled or not
 *
 * @type {boolean}
 */
GridProductView.prototype.areSpotlightTypeButtonsDisabled = false;

/**
 * Instance of a FolderCardMenu used to add the product to a folder
 *
 * @type {FolderCardMenu}
 */
GridProductView.prototype.folderCardMenu = null;

/**
 * The data used to represent/marshal a product
 *
 * @type {Object}
 */
GridProductView.prototype.data = null;

/**
 * View that controls a products' spotlight type buttons
 *
 * @type {SpotlightTypesView}
 */
GridProductView.prototype.spotlightTypesView = null;

/**
 * Sends data to the server to add this product to the end of the collection sort
 *
 * @returns {$.Deferred} The jQuery Deferred object to chain upon
 */
GridProductView.prototype.addToCollectionSort = function addToCollectionSort() {
    const $btn = this.$el.find('.js-append-to-collection');

    $btn.addClass('disabled');

    return $.ajax({
        data: {
            collection_ids: getCollectionId(),
            product_id: this.getProductId(),
        },
        method: 'POST',
        url: getCollectionSortPatchUrl(),
    })
        .done(
            $.proxy(function onAddToCollectionSortDone(data) {
                if (data.errors && data.errors.length) {
                    AlerterView.error(data.errors[0].message);
                } else {
                    $btn.removeClass('off');
                    $btn.addClass('on');
                    this.setIsMerched(true);
                    this.trigger('change', this);
                }
            }, this),
        )
        .fail((jqXHR) => {
            const data = jqXHR.responseJSON;
            if (data) {
                if (!data.errors || !data.errors.length) {
                    data.errors = [{ message: getDefaultErrorText() }];
                }
                for (let i = 0; i < data.errors.length; i += 1) {
                    AlerterView.error(data.errors[i].message);
                }
            } else {
                AlerterView.error();
            }
        })
        .always(() => {
            $btn.removeClass('disabled');
        });
};

/**
 * Disables the spotlight type buttons for this product
 */
GridProductView.prototype.disableSpotlightTypeButtons = function disableSpotlightTypeButtons() {
    this.spotlightTypesView.disableSpotlightTypeButtons();
};

/**
 * Enables the spotlight type buttons for this product
 */
GridProductView.prototype.enableSpotlightTypeButtons = function enableSpotlightTypeButtons() {
    this.spotlightTypesView.enableSpotlightTypeButtons();
};

/**
 * @returns {Object} The product's data from the backend
 */
GridProductView.prototype.getData = function getData() {
    return this.data;
};

/**
 * @returns {jQuery} The jQuery object representing the grid product
 */
GridProductView.prototype.getEl = function getEl() {
    return this.$el;
};

/**
 * @returns {jQuery} The event trigger element
 */
GridProductView.prototype.getEventTriggerEl = function getEventTriggerEl() {
    return this.$el.find('.js-event-trigger');
};

/**
 * @returns {number} The id of the product the GridProductView represents
 */
GridProductView.prototype.getProductId = function getProductId() {
    return this.data.id;
};

/**
 * Removes the folder trigger from the product's ui
 *
 * @param {boolean=} shouldRemoveImmediately Optional flag indicating the folder trigger should be
 *   removed immediately without waiting for the menu to be closed
 */
GridProductView.prototype.removeFolderTrigger = function removeFolderTrigger(shouldRemoveImmediately) {
    if (shouldRemoveImmediately) {
        if (this.folderCardMenu) {
            this.folderCardMenu.removeFolderTrigger();
            this.folderCardMenu.close();
        }
    } else if (this.folderCardMenu) {
        this.folderCardMenu.close().done(
            $.proxy(function onFolderCardMenuCloseDone() {
                this.folderCardMenu.removeFolderTrigger();
            }, this),
        );
    }
};

/**
 * Removes spotlight type pills from the product's ui
 */
GridProductView.prototype.removeSpotlightTypePills = function removeSpotlightTypePills() {
    this.$el.find('.js-tag-container').html('');
};

/**
 * Removes the event trigger from the DOM
 */
GridProductView.prototype.removeEventTrigger = function removeEventTrigger() {
    this.$el.find('.js-event-trigger').remove();
};

/**
 * Adds a tap target that triggers the add to folder action
 */
GridProductView.prototype.renderFolderTrigger = function renderFolderTrigger() {
    if (canRenderFolderIcons()) {
        this.folderCardMenu = new FolderCardMenu({
            productId: this.getProductId(),
            analyticsTrackingPosition: FolderCardPosition.PRODUCT_GRID,
            animation: FolderCardTransitionAnimations.flip,
            shouldCloseOnSelection: true,
            $appendTo: this.getEl().find('.js-grid-product-menu-wrapper'),
        });
        this.folderCardMenu.marshalFolderTrigger().insertBefore(this.getEl().find('.js-favorite-button'));
        this.folderCardMenu.bind();
    }
};

/**
 * Adds a target that emits a given event when clicked/tapped.
 *
 * @param {string} eventToEmit The name of the event to emit when the event trigger is clicked/tapped
 */
GridProductView.prototype.renderEventTrigger = function renderEventTrigger(eventToEmit) {
    const $eventTrigger = $('<div></div>', {
        class: 'event-trigger js-event-trigger',
        text: '+',
    });

    $eventTrigger.insertBefore(this.$el.find('.js-favorite-button'));

    $eventTrigger.on(
        'click',
        $.proxy(function onEventTriggerClick(e) {
            e.preventDefault();
            e.stopPropagation();

            this.$el.trigger($.Event(eventToEmit), this);
        }, this),
    );
};

/**
 * Renders the spotlight type buttons for this product based on the product's data
 */
GridProductView.prototype.renderSpotlightTypeButtons = function renderSpotlightTypeButtons() {
    this.spotlightTypesView.renderSpotlightTypeButtons();
};

/**
 * Renders the spotlight type wholesale assortment tag for this product based on the product's data
 */
GridProductView.prototype.renderSpotlightTypeTag = function renderSpotlightTypeTag() {
    this.spotlightTypesView.renderSpotlightTypeTag();
};

/**
 * Renders the Add to Top-N button on the product if possible
 */
GridProductView.prototype.renderAddToCollectionSortAction = function renderAddToCollectionSortAction() {
    if (getCollectionSortPatchUrl()) {
        let $sortButtonsContainer = this.$el.find('.js-collection-sort-buttons');
        if (!$sortButtonsContainer.length) {
            $sortButtonsContainer = $('<div></div>', {
                class: 'collection-sort-buttons js-collection-sort-buttons',
            });
            this.$el.find('.js-merch-buttons').append($sortButtonsContainer);
        }

        const $btn = $('<span></span>', {
            class: ['spotlight-button', 'js-append-to-collection', this.isMerched() ? 'on' : 'off'].join(' '),
        }).append(
            $('<span></span>', { class: 'cicon cicon-top96-stroke off' }),
            $('<span></span>', { class: 'cicon cicon-top96-fill on' }),
        );

        $sortButtonsContainer.html($btn);
    }
};

/**
 * Renders spotlight type pills on a grid product for easy removal
 */
GridProductView.prototype.renderSpotlightTypePills = function renderSpotlightTypePills() {
    const tags = [];

    for (let i = 0; i < this.data.spotlight_types.length; i += 1) {
        const pill = new Pill({
            value: this.data.spotlight_types[i],
        });
        pill.bind();
        tags.push(pill.getEl());
    }

    let $tagContainer = this.$el.find('.js-tag-container');
    if (!$tagContainer.length) {
        $tagContainer = $('<div></div>', {
            class: 'tag-container js-tag-container',
        });
        this.$el.find('.js-product-wrapper').prepend($tagContainer);
    }

    $tagContainer.html(tags);
    this.bindPills();
};

/**
 * @returns {boolean} True indicates the product is merch'd in the Top-N for the current collection
 */
GridProductView.prototype.isMerched = function isMerched() {
    return !!this.data.is_merched;
};

/**
 * Sends data to the server to remove this product from the collection sort
 *
 * @returns {$.Deferred} The jQuery Deferred object to chain upon
 */
GridProductView.prototype.removeFromCollectionSort = function removeFromCollectionSort() {
    const $btn = this.$el.find('.js-append-to-collection');

    $btn.addClass('disabled');

    return $.ajax({
        data: {
            collection_id: getCollectionId(),
            product_id: this.getProductId(),
        },
        method: 'POST',
        url: getCollectionSortDeleteUrl(),
    })
        .done(
            $.proxy(function onRemoveFromCollectionSortDone(data) {
                if (data.errors) {
                    AlerterView.error(data.errors[0].message);
                } else {
                    $btn.addClass('off');
                    $btn.removeClass('on');

                    this.setIsMerched(false);
                    this.trigger('change', this);
                }
            }, this),
        )
        .fail(() => {
            AlerterView.error(getDefaultErrorText());
        })
        .always(() => {
            $btn.removeClass('disabled');
        });
};

/**
 * @param {boolean} isMerched True indicates the product is merch'd in the Top-N for the current collection
 */
GridProductView.prototype.setIsMerched = function setIsMerched(isMerched) {
    this.data.is_merched = isMerched;
};

/**
 * Updates the product's spotlight type data based on the data provided and the action (toggle by default) being
 * taken.
 *
 * Please note that there is a delay in when the data is sent to the server so data can be batched to account for
 * rapid fire updates that can overwrite each other server side.
 *
 * @param {Array.<string>} spotlightTypeCodeArray Collection of spotlight type codes that are changing
 * @param {GridProductView.SpotlightTypeAction=} spotlightTypeAction The action to update for; toggle by default
 */
GridProductView.prototype.updateSpotlightTypeData = function updateSpotlightTypeData(
    spotlightTypeCodeArray,
    spotlightTypeAction,
) {
    this.spotlightTypesView.updateSpotlightTypeData(spotlightTypeCodeArray, spotlightTypeAction);
};

/**
 * Binds actions to the GridProduct
 */
GridProductView.prototype.bind = function bind() {
    const $spotlightTypeButtonContainer = this.spotlightTypesView.getEl();
    $spotlightTypeButtonContainer.on(
        'change',
        $.proxy(function onSpotlightTypeButtonContainerChange(e) {
            e.stopPropagation(); // do not bubble up
            this.trigger('change', this);
        }, this),
    );

    $spotlightTypeButtonContainer.on(
        'click',
        '.js-append-to-collection',
        $.proxy(function onAppendToCollectionClick(e) {
            e.preventDefault();
            e.stopPropagation();

            if (!$(e.currentTarget).hasClass('disabled')) {
                if (this.isMerched()) {
                    this.removeFromCollectionSort();
                } else {
                    this.addToCollectionSort();
                }
            }
        }, this),
    );
};

/**
 * Binds actions to the spotlight type pills
 */
GridProductView.prototype.bindPills = function bindPills() {
    const $pills = this.$el.find('.js-pill');

    $pills.on(
        'pill:remove',
        $.proxy(function onSpotlightPillRemove(e) {
            e.preventDefault();
            e.stopPropagation();

            const pillInstance = $(e.currentTarget).data('pill');
            this.updateSpotlightTypeData([
                {
                    code: pillInstance.getValue().value,
                    action: SpotlightTypesView.SpotlightTypeAction.REMOVE,
                },
            ]);
        }, this),
    );
};

/**
 * Triggers a new event while passing along any other additional data provided
 *
 * @param {string} type The new event's type
 * @param {Object=} data Additional data to be passed along as an argument to the event handler
 */
GridProductView.prototype.trigger = function trigger(type, data) {
    this.$el.trigger($.Event(type), data);
};

export default GridProductView;
